// API-Client gegen das C++-Backend.
// Bearer-Token wird im localStorage gespeichert und automatisch in jeden Request mitgegeben.

export const TOKEN_KEY = "trackme.token";
const NAME_KEY = "trackme.displayName";
const EMAIL_KEY = "trackme.email";

export type Category = "food" | "drink";
export type AmountUnit = "gram" | "milliliter" | "piece" | "portion";
export type MealType = "breakfast" | "lunch" | "dinner" | "snack" | "drink";

export interface Recipe {
    recipe_id: number;
    name: string;
    description?: string | null;
    category: Category;
    reference_amount: number;
    reference_unit: AmountUnit;
    protein: number;
    fat: number;
    sugar: number;
    kcal: number;
    barcode?: string | null;
    has_photo?: boolean;
}

export interface RecipeFull extends Recipe {
    created_by_user_id: number;
    created_at: number;
    updated_at: number;
}

export interface ConsumptionEntry {
    consumption_log_id: number;
    recipe_id: number;
    recipe_name: string;
    category: Category;
    consumed_at: number;
    amount: number;
    amount_unit: AmountUnit;
    meal_type: MealType;
    note?: string | null;
}

export interface ConsumptionAggregate {
    kcal_total: number;
    protein_total: number;
    fat_total: number;
    sugar_total: number;
    entry_count: number;
    drink_volume_ml_total: number;
    drink_count: number;
}

export interface DailyAggregate {
    date: string;     // YYYY-MM-DD
    kcal_total: number;
    protein_total: number;
    fat_total: number;
    sugar_total: number;
    entry_count: number;
}

export interface UserTarget {
    kcal_target: number;
    protein_target: number;
    fat_target: number;
    sugar_target: number;
    updated_at?: number;
    dow_overrides?: DowTargetOverride[];
}

export interface DowTargetOverride {
    dow: number;  // 0=Mo..6=So
    kcal_target: number;
    protein_target: number;
    fat_target: number;
    sugar_target: number;
}

// Berechnet das effektive Tagesziel fuer ein Datum (lokaler Wochentag).
export function effectiveTargetFor (t: UserTarget | null, day: Date): UserTarget | null {
    if (t === null) return null;
    const dow = (day.getDay () + 6) % 7;
    const o = t.dow_overrides?.find (x => x.dow === dow);
    if (o === undefined) return t;
    return {
        kcal_target: o.kcal_target,
        protein_target: o.protein_target,
        fat_target: o.fat_target,
        sugar_target: o.sugar_target,
    };
}

export interface RecipeUsageEntry {
    consumption_log_id: number;
    // user_id + display_name wurden absichtlich entfernt: der Server liefert
    // jetzt nur noch die eigene Recipe-Usage (siehe handleGetRecipeUsage).
    consumed_at: number;
    amount: number;
    amount_unit: AmountUnit;
    meal_type: MealType;
}

export interface PushSubscriptionInit {
    endpoint: string;
    p256dh: string;
    auth: string;
}

export interface Workout {
    workout_log_id: number;
    started_at: number;          // epoch s
    activity_type: string;
    name: string;
    duration_seconds: number;
    kcal: number;
    distance_m: number | null;
    avg_hr: number | null;
    max_hr: number | null;
    elevation_gain_m: number | null;
    note: string | null;
    source: string;              // "garmin" | "manual"
}

export interface WorkoutInput {
    started_at: number;
    activity_type: string;
    name?: string;
    duration_seconds: number;
    kcal?: number;
    distance_m?: number | null;
    avg_hr?: number | null;
    max_hr?: number | null;
    elevation_gain_m?: number | null;
    note?: string | null;
}

export interface WeightEntry {
    weighed_at: number;
    weight_kg: number;
    body_fat_pct: number | null;
    muscle_mass_kg: number | null;
    body_water_pct: number | null;
    bmi: number | null;
    source: string;
}

export interface MealTemplate {
    meal_template_id: number;
    name: string;
    default_meal_type: MealType;
}

export interface MealTemplateItem {
    meal_template_item_id: number;
    recipe_id: number;
    recipe_name: string;
    amount: number;
    amount_unit: AmountUnit;
}

export interface TopRecipe {
    recipe_id: number;
    recipe_name: string;
    category: Category;
    use_count: number;
}

export interface LoginResponse {
    token: string;
    user_id: number;
    email: string;
    display_name: string;
    expires_in: number;
}

export interface CreatedId {
    [key: string]: number;
}

export class ApiError extends Error {
    constructor (public status: number, message: string) {
        super (message);
    }
}

function getToken (): string | null {
    return localStorage.getItem (TOKEN_KEY);
}

// Escapes HTML in Server-/User-Strings, die in `innerHTML`-Templates landen.
// Frueher mehrfach kopiert in consumption.ts / recipes.ts / stats.ts.
// Note: bevorzugt textContent verwenden — escapeHtml ist nur fuer den Fall, dass
// wir wirklich HTML drum herum bauen und der Wert im Template-String einzuhaengen ist.
export function escapeHtml (s: string): string {
    return s.replace (/[&<>"']/g, c => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;",
    } as Record<string, string>)[c]);
}

// Container in eine Error-Box verwandeln. Verwendet textContent (nicht
// innerHTML) damit Server-/Exception-Strings keine HTML-Injektion erlauben.
export function renderError (container: HTMLElement, err: unknown): void {
    container.innerHTML = "";
    const div = document.createElement ("div");
    div.className = "error";
    div.textContent = (err instanceof Error) ? err.message : "Fehler";
    container.appendChild (div);
}

export function getDisplayName (): string | null {
    return localStorage.getItem (NAME_KEY);
}

export function getEmail (): string | null {
    return localStorage.getItem (EMAIL_KEY);
}

export function isLoggedIn (): boolean {
    return getToken () !== null;
}

export function setSession (token: string, displayName: string, email: string): void {
    localStorage.setItem (TOKEN_KEY, token);
    localStorage.setItem (NAME_KEY, displayName);
    localStorage.setItem (EMAIL_KEY, email);
}

export function clearSession (): void {
    localStorage.removeItem (TOKEN_KEY);
    localStorage.removeItem (NAME_KEY);
    localStorage.removeItem (EMAIL_KEY);
}

async function request<T> (method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};

    if (body !== undefined) {
        headers["Content-Type"] = "application/json";
    }

    const token = getToken ();

    if (token !== null) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch (`/api${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify (body) : undefined,
    });

    // 401 bedeutet zwingend "Session weg/abgelaufen" (alle Endpoints schicken
    // 403 fuer aktions-spezifische Authorisierungsfehler wie "falsches
    // aktuelles Passwort"). Deshalb hier global Logout + Redirect-Trigger.
    if (res.status === 401) {
        clearSession ();
        throw new ApiError (401, "Nicht angemeldet");
    }

    if (res.status === 204) {
        return undefined as unknown as T;
    }

    const text = await res.text ();
    // JSON.parse darf nicht uncaught werfen: ein non-JSON-Body (z.B. httplib's
    // text/plain "Payload Too Large" bei 413) wuerde sonst statt einer ApiError
    // einen SyntaxError werfen, den die View-Layer dann als generischen
    // "Fehler" statt mit Status anzeigt.
    let data: unknown = undefined;
    if (text.length > 0) {
        try { data = JSON.parse (text); }
        catch { data = undefined; }
    }

    if (!res.ok) {
        const msg = (data && typeof data === "object" && "error" in data)
            ? (data as { error: string }).error
            : (text.length > 0 && text.length < 200 ? text : `HTTP ${res.status}`);
        throw new ApiError (res.status, msg);
    }

    return data as T;
}

export const api = {
    login: (email: string, password: string) =>
        request<LoginResponse> ("POST", "/login", { email, password }),
    logout: () => request<void> ("POST", "/logout"),
    me: () => request<{ user_id: number; display_name: string }> ("GET", "/me"),

    createUser: (email: string, displayName: string, password: string) =>
        request<{ user_id: number; email: string; display_name: string }> ("POST", "/users", {
            email, display_name: displayName, password,
        }),

    listRecipes: (category: Category = "food") =>
        request<Recipe[]> ("GET", `/recipes?category=${encodeURIComponent (category)}`),
    searchRecipes: (query: string) =>
        request<Recipe[]> ("GET", `/recipes?q=${encodeURIComponent (query)}`),
    recentRecipes: () => request<Recipe[]> ("GET", "/recipes/recent"),
    getRecipeByBarcode: (code: string) =>
        request<RecipeFull> ("GET", `/recipes/by-barcode/${encodeURIComponent (code)}`),
    getRecipe: (id: number) =>
        request<RecipeFull> ("GET", `/recipes/${id}`),
    createRecipe: (r: Omit<Recipe, "recipe_id">) =>
        request<CreatedId> ("POST", "/recipes", r),
    updateRecipe: (id: number, r: Omit<Recipe, "recipe_id">) =>
        request<void> ("PUT", `/recipes/${id}`, r),
    deleteRecipe: (id: number) =>
        request<void> ("DELETE", `/recipes/${id}`),

    listUsers: () =>
        request<Array<{ user_id: number; email: string; display_name: string }>> ("GET", "/users"),
    changePassword: (email: string, currentPassword: string, newPassword: string) =>
        request<void> ("POST", "/me/password", {
            email, current_password: currentPassword, new_password: newPassword,
        }),

    listConsumption: (from: number, to: number) =>
        request<ConsumptionEntry[]> ("GET", `/consumption?from=${from}&to=${to}`),
    aggregateConsumption: (from: number, to: number) =>
        request<ConsumptionAggregate> ("GET", `/consumption/aggregate?from=${from}&to=${to}`),
    consumptionByDay: (from: number, to: number) => {
        // Browser-Timezone mitschicken, damit Server-Side Day-Aggregate per
        // lokalem Tag gruppiert und nicht per UTC-Tag. Sonst landen Eintraege
        // um 23:30 lokal (= 22:30 UTC) und 00:30 nächstem Tag lokal (= 23:30 UTC,
        // beide UTC same day) im UI auf verschiedenen Tagen aber im Chart auf
        // demselben — widerspruechliche Zahlen.
        const tz = encodeURIComponent (Intl.DateTimeFormat ().resolvedOptions ().timeZone || "UTC");
        return request<DailyAggregate[]> ("GET", `/consumption/by-day?from=${from}&to=${to}&tz=${tz}`);
    },

    getTarget: () => request<UserTarget | null> ("GET", "/me/target"),
    putTarget: (t: UserTarget) => request<void> ("PUT", "/me/target", t),
    putTargetDow: (dow: number, t: Omit<DowTargetOverride, "dow">) =>
        request<void> ("PUT", `/me/target/dow/${dow}`, t),
    deleteTargetDow: (dow: number) =>
        request<void> ("DELETE", `/me/target/dow/${dow}`),

    getDailyNote: (date: string) =>
        request<{ note: string; updated_at: number | null }> ("GET", `/me/daily-note/${date}`),
    putDailyNote: (date: string, note: string) =>
        request<void> ("PUT", `/me/daily-note/${date}`, { note }),

    postWeight: (entry: {
        weighed_at?: number;
        weight_kg: number;
        body_fat_pct?: number;
        muscle_mass_kg?: number;
        body_water_pct?: number;
        bmi?: number;
        source?: string;
    }) => request<void> ("POST", "/me/weight", entry),
    listWeight: (from: number, to: number) =>
        request<WeightEntry[]> ("GET", `/me/weight?from=${from}&to=${to}`),

    getActivity: (dateIso: string) =>
        request<{ steps: number; floors: number; kcal_burned_total: number; kcal_burned_active: number; source: string }>
            ("GET", `/me/activity?date=${dateIso}`),
    getSleep: (dateIso: string) =>
        request<{ total_seconds: number; deep_seconds: number; light_seconds: number; rem_seconds: number;
                  awake_seconds: number; sleep_score: number | null;
                  start_at: number; end_at: number; source: string }>
            ("GET", `/me/sleep?date=${dateIso}`),

    listMealTemplates: () => request<MealTemplate[]> ("GET", "/me/meal-templates"),
    listMealTemplateItems: (id: number) =>
        request<MealTemplateItem[]> ("GET", `/me/meal-templates/${id}/items`),
    createMealTemplate: (t: {
        name: string;
        default_meal_type: MealType;
        items: Array<{ recipe_id: number; amount: number; amount_unit: AmountUnit }>;
    }) => request<{ meal_template_id: number }> ("POST", "/me/meal-templates", t),
    deleteMealTemplate: (id: number) =>
        request<void> ("DELETE", `/me/meal-templates/${id}`),
    // Server-seitige Transaktion: alle Items des Templates werden in einem
    // einzigen DB-Commit angelegt. Bei Fehler in einem Item rollt der Server
    // alles zurueck — kein halb-appliedes Template mehr im consumption_log.
    applyMealTemplate: (id: number, payload: { consumed_at: number; meal_type?: MealType }) =>
        request<{ inserted: number }> ("POST", `/me/meal-templates/${id}/apply`, payload),

    topRecipes: (from: number, to: number) =>
        request<TopRecipe[]> ("GET", `/stats/top-recipes?from=${from}&to=${to}`),
    createConsumption: (entry: {
        recipe_id: number;
        consumed_at?: number;
        amount: number;
        amount_unit: AmountUnit;
        meal_type: MealType;
        note?: string;
    }) => request<CreatedId> ("POST", "/consumption", entry),
    updateConsumption: (id: number, entry: {
        recipe_id: number;
        consumed_at?: number;
        amount: number;
        amount_unit: AmountUnit;
        meal_type: MealType;
        note?: string;
    }) => request<void> ("PUT", `/consumption/${id}`, entry),
    deleteConsumption: (id: number) =>
        request<void> ("DELETE", `/consumption/${id}`),

    getRecipeUsage: (id: number) =>
        request<RecipeUsageEntry[]> ("GET", `/recipes/${id}/usage`),

    uploadRecipePhoto: async (id: number, blob: Blob) => {
        const token = localStorage.getItem (TOKEN_KEY);
        const headers: Record<string, string> = { "Content-Type": "image/jpeg" };
        if (token !== null) headers["Authorization"] = `Bearer ${token}`;
        const res = await fetch (`/api/recipes/${id}/photo`, {
            method: "POST",
            headers,
            body: blob,
        });
        // 401 hier genauso behandeln wie im request<T>-Wrapper, sonst bleibt
        // der User in der UI "eingeloggt" obwohl die Session weg ist.
        if (res.status === 401) {
            clearSession ();
            throw new ApiError (401, "Nicht angemeldet");
        }
        if (!res.ok) throw new ApiError (res.status, `HTTP ${res.status}`);
    },
    deleteRecipePhoto: (id: number) =>
        request<void> ("DELETE", `/recipes/${id}/photo`),

    // Laedt das Rezept-Foto mit Authorization-Header (statt Token in der URL)
    // und gibt eine object-URL (blob:) zurueck, die direkt an <img src> kann.
    // So landet der Session-Token nicht mehr in Referer/Proxy-Logs/History.
    // Der Aufrufer MUSS die URL nach Gebrauch via URL.revokeObjectURL freigeben.
    // null => kein Foto (404) oder anderer Fehler.
    recipePhotoObjectUrl: async (id: number): Promise<string | null> => {
        const token = localStorage.getItem (TOKEN_KEY);
        const headers: Record<string, string> = {};
        if (token !== null) headers["Authorization"] = `Bearer ${token}`;
        const res = await fetch (`/api/recipes/${id}/photo`, { headers });
        // Fail-soft: ein Foto ist reine Anzeige. Bei JEDEM Fehler (404, 401,
        // 5xx) null zurueckgeben und den Platzhalter stehen lassen — bewusst
        // KEIN clearSession() hier. Sonst wuerde ein abgelaufener Token waehrend
        // eines passiven Foto-Loads die Session still beenden, ohne dass die UI
        // reagiert. Ein echter API-Call raeumt die tote Session ohnehin auf.
        if (!res.ok) return null;
        return URL.createObjectURL (await res.blob ());
    },

    pushSubscribe: (sub: PushSubscriptionInit) =>
        request<void> ("POST", "/me/push-subscribe", sub),
    pushUnsubscribe: (endpoint: string) =>
        request<void> ("DELETE", "/me/push-subscribe", { endpoint }),

    getGarminStatus: () =>
        request<{ configured: boolean;
                  last_sync_at: number | null;
                  last_successful_sync_at: number | null;
                  last_sync_error: string | null }>
            ("GET", "/me/garmin/status"),

    // Stoesst einen Sync auf dem Server an. Antwort kommt sofort (202);
    // der eigentliche Sync laeuft im Hintergrund — Status danach pollen.
    triggerGarminSync: (days?: number) =>
        request<{ ok: boolean; queued: boolean }>
            ("POST", days ? `/me/garmin/sync?days=${days}` : "/me/garmin/sync"),

    // Workouts (einzelne Sport-Aktivitaeten): from/to als Unix-Epoch in Sekunden.
    // Garmin-Eintraege sind read-only (source="garmin"), manuelle editierbar.
    listWorkouts: (from: number, to: number) =>
        request<Workout[]> ("GET", `/me/workouts?from=${from}&to=${to}`),
    createWorkout: (w: WorkoutInput) =>
        request<{ workout_log_id: number }> ("POST", "/me/workouts", w),
    updateWorkout: (id: number, w: WorkoutInput) =>
        request<void> ("PUT", `/me/workouts/${id}`, w),
    deleteWorkout: (id: number) =>
        request<void> ("DELETE", `/me/workouts/${id}`),
};
